import type { SponsorDto } from '@ukinfo/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useConfirm } from '../ConfirmDialog';
import { Button, Dialog, Field, Input, useToast } from '../ui';

/** Ansprechpartner eines Sponsors - anlegen und entfernen ohne Umweg. */
export function SponsorContacts({ sponsor }: { sponsor: SponsorDto }): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const { confirm, confirmDialog } = useConfirm();
  const mayEdit = can('sponsoring_contracts', 'update');

  const [open, setOpen] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('');

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['sponsors'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api.post(`/api/sponsors/${sponsor.id}/contacts`, {
        firstName,
        lastName,
        email: email || null,
        phone: phone || null,
        role: role || null,
      }),
    onSuccess: async () => {
      await refresh();
      toast.show('Ansprechpartner hinzugefügt');
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setRole('');
      setOpen(false);
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen', 'error'),
  });

  const remove = useMutation({
    mutationFn: (contactId: string) => api.del(`/api/sponsor-contacts/${contactId}`),
    onSuccess: async () => {
      await refresh();
      toast.show('Ansprechpartner entfernt');
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Entfernen fehlgeschlagen', 'error'),
  });

  const entfernen = async (contact: SponsorDto['contacts'][number]): Promise<void> => {
    const ok = await confirm({
      title: 'Ansprechpartner entfernen?',
      message: `${contact.firstName} ${contact.lastName} wird als Ansprechpartner von ${sponsor.companyName} entfernt.`,
      confirmLabel: 'Ja, entfernen',
      tone: 'danger',
    });
    if (ok) remove.mutate(contact.id);
  };

  return (
    <div className="space-y-1">
      {confirmDialog}
      {sponsor.contacts.length === 0 ? (
        <p className="text-sm text-slate-400">Kein Ansprechpartner hinterlegt</p>
      ) : (
        <ul className="space-y-0.5">
          {sponsor.contacts.map((contact) => (
            <li key={contact.id} className="flex flex-wrap items-center gap-2 text-sm">
              <span className="text-slate-700">
                {contact.firstName} {contact.lastName}
                {contact.role && <span className="text-slate-500"> · {contact.role}</span>}
              </span>
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="text-brand-700 hover:underline">
                  {contact.email}
                </a>
              )}
              {contact.phone && <span className="text-slate-500">{contact.phone}</span>}
              {mayEdit && (
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-rose-600"
                  onClick={() => void entfernen(contact)}
                  aria-label={`${contact.firstName} ${contact.lastName} entfernen`}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {mayEdit && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          + Ansprechpartner
        </button>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={`Ansprechpartner für ${sponsor.companyName}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => add.mutate()}
              loading={add.isPending}
              disabled={!firstName || !lastName}
            >
              Hinzufügen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" required>
              {(id) => (
                <Input id={id} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              )}
            </Field>
            <Field label="Nachname" required>
              {(id) => (
                <Input id={id} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              )}
            </Field>
          </div>
          <Field label="Funktion" hint="z. B. Geschäftsführung, Marketing">
            {(id) => <Input id={id} value={role} onChange={(e) => setRole(e.target.value)} />}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="E-Mail">
              {(id) => (
                <Input
                  id={id}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>
            <Field label="Telefon">
              {(id) => <Input id={id} value={phone} onChange={(e) => setPhone(e.target.value)} />}
            </Field>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
